<template>
  <div class="pa-4">
    <!-- Loading state -->
    <template v-if="loading">
      <v-row align="center" justify="center">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">{{ $t("common.action.loading") }}</div>
        </v-col>
      </v-row>
    </template>

    <!-- Empty catalog state (no products at all) -->
    <template v-else-if="showEmptyCatalogMessage">
      <v-row class="mt-3 mb-5 mr-1">
        <v-col cols="12" class="d-flex align-center ga-3">
          <v-spacer></v-spacer>

          <!-- Actions dropdown for admin functions - only Import Products -->
          <v-menu
            v-if="hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER])"
            v-feature="PlatformFeature.INVENTORY"
          >
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" color="secondary" append-icon="mdi-chevron-down">
                {{ $t("common.action.actions") }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="openImportDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-package-down</v-icon>
                  {{ $t("common.action.importProducts") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="openImportPricesDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-tag-arrow-down</v-icon>
                  {{ $t("common.action.importPrices") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="openExportPricesDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-tag-arrow-up</v-icon>
                  {{ $t("common.action.exportPrices") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-card height="24rem" color="background" class="d-flex align-center justify-center">
        <v-card-text>
          <v-row>
            <v-img
              src="@/shared/assets/add_product_landing.png"
              max-height="150px"
              min-height="75px"
            />
          </v-row>
          <v-row class="mx-10">
            <v-col cols="10" offset="1">
              <v-card-text class="text-grey-darken-1 text-body-2 pb-0 text-center">
                {{ $t("common.action.createAProductInstantly") }}
              </v-card-text>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-center">
            <v-btn color="primary" class="my-5" prepend-icon="mdi-plus" @click="createProducts">
              {{ $t("common.action.createProduct") }}
            </v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

    <!-- Products view (filtered or not) -->
    <template v-else>
      <v-row class="mt-3 mb-5 mr-1">
        <v-col cols="12" lg="12" class="d-flex align-center ga-3">
          <v-spacer></v-spacer>

          <!-- Admin Actions Dropdown -->
          <v-menu
            v-if="hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER])"
            v-feature="PlatformFeature.INVENTORY"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-feature="PlatformFeature.INVENTORY"
                v-bind="props"
                color="secondary"
                append-icon="mdi-chevron-down"
              >
                {{ $t("common.action.actions") }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-feature="PlatformFeature.INVENTORY"
                @click="openExportDialog(ExportContext.QR_CODES)"
              >
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-qrcode</v-icon>
                  {{ $t("common.action.printQRCodes") }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-feature="PlatformFeature.INVENTORY" @click="openImportDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-package-down</v-icon>
                  {{ $t("common.action.importProducts") }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-feature="PlatformFeature.INVENTORY"
                @click="openExportDialog(ExportContext.CSV)"
              >
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-package-up</v-icon>
                  {{ $t("common.action.exportProducts") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-feature="PlatformFeature.INVENTORY" @click="openImportPricesDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-tag-arrow-down</v-icon>
                  {{ $t("common.action.importPrices") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-feature="PlatformFeature.INVENTORY" @click="openExportPricesDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-tag-arrow-up</v-icon>
                  {{ $t("common.action.exportPrices") }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-feature="PlatformFeature.INVENTORY" @click="openBulkAdjustDialog">
                <v-list-item-title>
                  <v-icon class="mr-2">mdi-table-edit</v-icon>
                  {{ $t("common.action.bulkAdjustInventory") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Create Product Button - Always visible -->
          <v-btn color="primary" prepend-icon="mdi-plus" @click="createProducts">
            {{ $t("common.action.createProduct") }}
          </v-btn>
        </v-col>
        <v-col cols="12" lg="12">
          <!-- Stock Alert Banners -->
          <low-stock-alert v-feature="PlatformFeature.INVENTORY" class="mb-4" />
          <out-of-stock-alert v-feature="PlatformFeature.INVENTORY" class="mb-4" />

          <!-- Product Search Component -->
          <product-search-component />

          <!-- No Results Message for filtered view -->
          <v-card v-if="showFilteredNoResults" class="pa-4 text-center">
            <v-icon icon="mdi-filter-off" size="large" color="grey" class="mb-2"></v-icon>
            <div class="text-h6">{{ $t("common.noResultsFound") }}</div>
            <div class="text-body-2 mb-4">
              {{ $t("common.tryDifferentFilters") }}
            </div>
            <v-btn color="primary" @click="clearAllFilters">
              {{ $t("common.action.clearAllFilters") }}
            </v-btn>
          </v-card>

          <!-- Product Table with Contained Loading State (only show if we have products) -->
          <product-table v-else />
        </v-col>
      </v-row>
    </template>

    <export-products-dialog
      ref="exportProductsDialog"
      :context="exportProductsContext"
      @confirm="handleExportProducts"
    />
    <ExportPricesDialog ref="exportPricesDialog" @confirm="handleExportPrices" />

    <ProductQRCodesExport v-if="exportQRCodeProducts" ref="qrRef" :products="products" />
  </div>
</template>

<script lang="ts" setup>
/**
 * ProductListPage
 *
 * This page persists filter state when navigating away and returning,
 * using the Pages.PRODUCTS enum value as the key in the route filter map.
 *
 * The filter persistence works by:
 * 1. Saving the current filter state to the store's routeFilterMap when leaving the page
 * 2. Restoring filters from routeFilterMap when returning to the page
 * 3. The filter state is also persisted to localStorage for survival across page reloads
 */

import { computed, onMounted, onUnmounted, ref } from "vue";
import ProductTable from "@/catalog/components/product/ProductTable.vue";
import ProductSearchComponent from "@/catalog/components/product/ProductSearchComponent.vue";
import ExportProductsDialog from "@/catalog/components/product/ExportProductsDialog.vue";
import ExportPricesDialog from "@/catalog/components/product/ExportPricesDialog.vue";
import { useProductsStore } from "@/catalog/stores/products";
import { usePricesStore } from "@/catalog/stores/prices";
import { useRouter } from "vue-router";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { Pages } from "@/shared/enums/route.enum";
import LowStockAlert from "@/catalog/components/inventory/LowStockAlert.vue";
import OutOfStockAlert from "@/catalog/components/inventory/OutOfStockAlert.vue";
import ProductQRCodesExport from "@/catalog/components/product/ProductQRCodesExport.vue";
import { nextTick } from "vue";
import type { ComponentPublicInstance } from "vue";
import { buildProductQRCodes } from "@/catalog/utils/productQRCodes.utils";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import i18n from "@/app/plugins/i18n";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import type { Product, ProductFilterCondition } from "@/catalog/types/products";
import type { QRCodeProduct } from "@/catalog/types/qrCodeExport";
import type { PriceExportScope } from "@nectarpay/api-contracts";
const productsStore = useProductsStore();
const pricesStore = usePricesStore();
const accountStore = useAccountStore();
const locationStore = useLocationStore();
// Loading state for initial page load
const loading = computed(() => productsStore.loading);
const listLoading = computed(() => productsStore.listLoading);
const hasProducts = ref(false);
// Add a flag to check if the user has actually filtered the products
const hasActiveFilters = computed(() => {
  const filterCondition = productsStore.filterProductCondition || {};
  return Object.keys(filterCondition).some(
    (key) => key !== "active" || filterCondition.active !== true,
  );
});

const locationBasedInventoryEnabled = computed(() => {
  return (
    accountStore.account?.accountSettings?.inventory?.location_based_inventory_enabled === true
  );
});

const hasEffectiveLocationIdFilter = computed(() => {
  if (!locationBasedInventoryEnabled.value) return false;

  const condition = productsStore.filterProductCondition || {};
  if ("locationId" in condition) {
    return typeof condition.locationId === "string" && Boolean(condition.locationId);
  }

  return Boolean(locationStore.selectedLocation?.id);
});
// Check if we're showing an empty catalog or a filtered view with no results
const showEmptyCatalogMessage = computed(() => {
  // Only show the empty catalog message if:
  // 1. We have checked and confirmed there are no products (hasProducts is false)
  // 2. The current product list is empty
  // 3. There are no active filters (so we're looking at the whole catalog)
  // 4. We're not currently loading
  return (
    !hasProducts.value &&
    !productsStore.products.length &&
    !hasActiveFilters.value &&
    !loading.value &&
    !listLoading.value
  );
});
// Check if we're showing filtered results with no products
const showFilteredNoResults = computed(() => {
  // Show the "no results for filters" message if:
  // 1. We have no products in the current view
  // 2. We do have active filters (including an explicit/implicit locationId search)
  // 3. We're not currently loading
  // 4. We know that the catalog itself has products (hasProducts is true)
  return (
    !productsStore.products.length &&
    (hasActiveFilters.value || hasEffectiveLocationIdFilter.value) &&
    !loading.value &&
    !listLoading.value &&
    hasProducts.value
  );
});
const router = useRouter();
const exportProductsDialog = ref<null | { open: () => void; close?: () => void }>(null);
const exportPricesDialog = ref<null | { open: () => void; close?: () => void }>(null);
const exportProductsContext = ref("");
const exportQRCodeProducts = ref(false);
const qrRef = ref<ComponentPublicInstance | null>(null);
const products = ref<Record<string, QRCodeProduct>>({});

const enum ExportContext {
  QR_CODES = "qr-codes",
  CSV = "csv",
}

onMounted(async () => {
  // Use route name instead of path for more reliability
  productsStore.restoreRouteFilters(Pages.PRODUCTS);
  const savedFilters = productsStore.routeFilterMap[Pages.PRODUCTS] || {};
  if (savedFilters && typeof savedFilters === "object" && !Array.isArray(savedFilters)) {
    const nextSavedFilters: ProductFilterCondition = { ...savedFilters };
    delete nextSavedFilters.locationId;
    productsStore.routeFilterMap[Pages.PRODUCTS] = nextSavedFilters;
    productsStore.setFilterProductCondition({ active: true, ...nextSavedFilters });
  }
  await loadProducts();
});

onUnmounted(() => {
  // Use route name instead of path for more reliability
  const filtersToPersist: ProductFilterCondition = { ...productsStore.filterProductCondition };
  delete filtersToPersist.locationId;
  productsStore.setFilterProductCondition(filtersToPersist);
  productsStore.saveRouteFilters(Pages.PRODUCTS);
});

const loadProducts = async () => {
  // On initial load, we'll use the loading flag
  productsStore.loading = true;

  try {
    // Make sure we're using the stored filters from the route
    const savedFiltersFromRoute = productsStore.routeFilterMap[Pages.PRODUCTS] || {};
    const savedFilters: ProductFilterCondition = { active: true, ...savedFiltersFromRoute };
    delete savedFilters.locationId;

    // Check if we have saved filters beyond the default active filter - if we do, we're coming back to a filtered view
    const hasFilteredView = Object.keys(savedFilters).some(
      (key) => key !== "active" || savedFilters.active !== true,
    );

    // If we have filters, first check if any products exist in the catalog
    // by doing a query without filters
    if (hasFilteredView) {
      const catalogCheck = await productsStore.checkIfProductsExist();
      hasProducts.value = !!catalogCheck;

      // Then do the filtered search to get filtered products
      await productsStore.searchProducts({ filters: savedFilters }, false);
    } else {
      // Normal case - just load all products
      const catalogCheck = await productsStore.checkIfProductsExist();
      hasProducts.value = !!catalogCheck;
      await productsStore.searchProducts({}, true);
    }

    // Re-apply saved filters to ensure they're not lost during the search operation
    if (hasFilteredView) {
      productsStore.setFilterProductCondition(savedFilters);
    }
  } finally {
    // Ensure loading is set to false in case of errors
    productsStore.loading = false;
  }
};

const createProducts = () => {
  router.push({ path: "/create/product" });
};

const openImportDialog = () => {
  router.push({ name: "importData", query: { create: "products" } });
};

const openImportPricesDialog = () => {
  router.push({ name: "importData", query: { create: "prices" } });
};

const openBulkAdjustDialog = () => {
  router.push({ name: "importData", query: { create: "inventory_adjustments" } });
};

const openExportPricesDialog = () => {
  exportPricesDialog.value?.open();
};

const openExportDialog = (context: string) => {
  exportProductsContext.value = context;
  exportProductsDialog.value?.open();
};

const handleExportProducts = async () => {
  try {
    // Just close the dialog if the close method exists
    if (exportProductsDialog.value && typeof exportProductsDialog.value.close === "function") {
      exportProductsDialog.value.close();
    }

    if (exportProductsContext.value === ExportContext.QR_CODES) {
      handleExportProductQRCodes();
    } else {
      // Call the exportProducts method from the store
      await productsStore.exportProducts();
    }
  } catch (error) {
    // Keep error handling but remove console logging
    useSnackBarStore().toastError({
      message: i18n.global.t("common.action.exportError"),
      error: getErrorMessage(error),
    });
  }
};

const handleExportPrices = async (priceScope: PriceExportScope) => {
  exportPricesDialog.value?.close?.();
  await pricesStore.exportPrices(productsStore.filterProductCondition, priceScope);
};

const handleExportProductQRCodes = async () => {
  try {
    const retrieveProducts = (await productsStore.retrieveQRCodeProducts()) ?? [];
    const mappedProducts = Object.fromEntries(
      retrieveProducts.map((product) => [product.id, product]),
    );
    products.value = mappedProducts;
    exportQRCodeProducts.value = true;

    // Wait for the component to be mounted and rendered
    await nextTick();

    // Get the QR codes page content
    const qrElement = qrRef.value?.$el as HTMLElement;
    if (qrElement) {
      const canvases = qrElement.querySelectorAll("canvas");

      canvases.forEach((canvas) => {
        const img = document.createElement("img");
        img.src = canvas.toDataURL("image/png");
        img.width = canvas.width;
        img.height = canvas.height;
        canvas.replaceWith(img);
      });

      // Create a complete HTML document
      const htmlContent = buildProductQRCodes(qrElement.outerHTML);

      // Create and download the file
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "product-qr-codes.html";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    // Reset the state
    exportQRCodeProducts.value = false;
    useSnackBarStore().toastSuccess({
      message: i18n.global.t("qrCodeGeneration.success"),
    });
  } catch (error) {
    useSnackBarStore().toastError({
      message: i18n.global.t("qrCodeGeneration.error"),
      error: getErrorMessage(error),
    });
  }
};

// Method to clear all filters and show all products
const clearAllFilters = () => {
  productsStore.clearRouteFilters(Pages.PRODUCTS);
  productsStore.setFilterProductCondition({ active: true });
  productsStore.searchProducts({}, false);
};
</script>
